/**
 * Navigatr.ts - Navigatr - Universal Navigation & View State Management
 * 
 * CHANGE HISTORY:
 * - 251021_0517 [8786ca9] danielbeer: feat: apply authr pattern to all 43 remaining packages
 * - 251021_0447 [ce7b5e6] danielbeer: feat: automate TypeScript and Actr integration for all packages
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
export class Navigatr {
    appName: any;
  options: Record<string, unknown>;
  state: any;
  private actr: any;
  private navigationStack: any[];
  private forwardStack: any[];
  private currentIndex: number;
  private menus: Map<string, any>;
  private breadcrumbs: any[];

  constructor(appName, options = {}) {
    this.actr = options.actr;
    this.appName = appName;
    this.options = {
      expirationTime: 24 * 60 * 60 * 1000, // 24 hours default
      maxRecentItems: 10,
      maxHistorySize: 50,
      useDefaults: true,
      defaults: {},
      storageKey: `navigatr_${appName}`,
      autoSave: true,
      ...options
    };

    this.state = {
      currentView: null,
      lastUsed: {},
      recentViews: {},
      lastUpdated: Date.now()
    };

    // Navigation history stack (browser-style)
    this.navigationStack = [];
    this.forwardStack = [];
    this.currentIndex = -1;

    // Menu registry
    this.menus = new Map();

    // Breadcrumb trail
    this.breadcrumbs = [];

    this.loadState();
    
    // Register actions with Actr if available
    if (this.actr) {
      this.registerActions();
    }
  }
  
  /**
   * Register Navigatr actions with Actr
   * 
   * ARCHITECTURE NOTE: Navigatr uses HYBRID pattern
   * 
   * Why Hybrid:
   * 1. Navigation State: User navigation state is persisted
   * 2. Dynamic Operations: Runtime view switching
   * 3. State Entity Management: Navigation history tracked
   * 
   * Pattern: Hybrid (Actions + Selective CRUD)
   * - Actions: For navigation operations (setView, goBack)
   * - CRUD: For navigation state persistence
   */
  registerActions(): void {
    if (!this.actr) return;
    
    // setView action
    this.actr.register('navigatr:set-view', {
      handler: async (params) => {
        return this.setView(params.type, params.data);
      },
      description: 'Set current view'
    });
    
    // Navigation actions
    this.actr.register('navigatr:push', {
      handler: async (params) => {
        return this.push(params.route, params.state);
      },
      description: 'Push route to navigation stack'
    });
    
    this.actr.register('navigatr:back', {
      handler: async () => {
        return this.back();
      },
      description: 'Navigate back'
    });
    
    this.actr.register('navigatr:forward', {
      handler: async () => {
        return this.forward();
      },
      description: 'Navigate forward'
    });
    
    // Menu actions
    this.actr.register('navigatr:register-menu', {
      handler: async (params) => {
        return this.registerMenu(params.id, params.config);
      },
      description: 'Register menu configuration'
    });
    
    this.actr.register('navigatr:get-menu', {
      handler: async (params) => {
        return this.getMenu(params.id);
      },
      description: 'Get menu configuration'
    });
    
    // Selective CRUD for navigation state
    this.actr.registerCRUD('navigation-state', {
      create: async (data) => {
        this.state = data;
        this.saveState();
        return this.state;
      },
      
      read: async (id) => {
        return this.state;
      },
      
      update: async (id, data) => {
        this.state = { ...this.state, ...data };
        this.saveState();
        return this.state;
      },
      
      delete: async (id) => {
        this.clearState();
        return { id };
      }
    });
  }

  /**
   * Set current view
   */
  setView(type, data): any {
    this.state.currentView = {
      type,
      data,
      timestamp: Date.now()
    };

    this.state.lastUpdated = Date.now();

    // Also track in recent views
    this.addToRecent(type, data);

    if (this.options.autoSave) {
      this.saveState();
    }

    return this.state.currentView;
  }

  /**
   * Get current view
   */
  getView(): string {
    if (this.isExpired() && this.options.useDefaults) {
      return this.getDefault('view');
    }

    return this.state.currentView;
  }

  /**
   * Set last used item of a type
   */
  setLastUsed(type, data): any {
    this.state.lastUsed[type] = {
      type,
      data,
      timestamp: Date.now()
    };

    this.state.lastUpdated = Date.now();

    // Also add to recent views
    this.addToRecent(type, data);

    if (this.options.autoSave) {
      this.saveState();
    }

    return this.state.lastUsed[type];
  }

  /**
   * Get last used item of a type
   */
  getLastUsed(type): string {
    if (this.isExpired() && this.options.useDefaults) {
      return this.getDefault(type);
    }

    return this.state.lastUsed[type] || null;
  }

  /**
   * Add to recent views
   */
  addToRecent(type, data): any {
    if (!this.state.recentViews[type]) {
      this.state.recentViews[type] = [];
    }

    const item = {
      type,
      data,
      timestamp: Date.now()
    };

    // Remove duplicates (same data)
    this.state.recentViews[type] = this.state.recentViews[type].filter(
      existing => JSON.stringify(existing.data) !== JSON.stringify(data)
    );

    // Add to beginning
    this.state.recentViews[type].unshift(item);

    // Limit to max items
    if (this.state.recentViews[type].length > this.options.maxRecentItems) {
      this.state.recentViews[type] = this.state.recentViews[type].slice(
        0,
        this.options.maxRecentItems
      );
    }
  }

  /**
   * Get recent views of a type
   */
  getRecentViews(type, limit = null): string {
    const recent = this.state.recentViews[type] || [];
    
    if (limit) {
      return recent.slice(0, limit);
    }

    return recent;
  }

  /**
   * Check if state is expired
   */
  isExpired(): boolean {
    if (this.options.expirationTime === Infinity) {
      return false;
    }

    const now = Date.now();
    const elapsed = now - this.state.lastUpdated;

    return elapsed > this.options.expirationTime;
  }

  /**
   * Get default value
   */
  getDefault(key): string {
    return this.options.defaults[key] || null;
  }

  /**
   * Use defaults (reset to defaults)
   */
  useDefaults(): any {
    this.state.currentView = this.getDefault('view');
    this.state.lastUsed = {};
    this.state.recentViews = {};
    this.state.lastUpdated = Date.now();

    if (this.options.autoSave) {
      this.saveState();
    }
  }

  /**
   * Set expiration time
   */
  setExpiration(ms): any {
    this.options.expirationTime = ms;
  }

  /**
   * Set defaults
   */
  setDefaults(defaults): any {
    this.options.defaults = { ...this.options.defaults, ...defaults };
  }

  /**
   * Save state to localStorage
   */
  saveState(): any {
    try {
      const serialized = JSON.stringify(this.state);
      localStorage.setItem(this.options.storageKey, serialized);
    } catch (error) {
      console.warn('Failed to save navigation state:', error);
    }
  }

  /**
   * Load state from localStorage
   */
  loadState(): any {
    try {
      const saved = localStorage.getItem(this.options.storageKey);
      if (saved) {
        this.state = JSON.parse(saved);

        // Check if expired
        if (this.isExpired() && this.options.useDefaults) {
          this.useDefaults();
        }
      }
    } catch (error) {
      console.warn('Failed to load navigation state:', error);
    }
  }

  /**
   * Clear all state
   */
  clearState(): any {
    this.state = {
      currentView: null,
      lastUsed: {},
      recentViews: {},
      lastUpdated: Date.now()
    };

    try {
      localStorage.removeItem(this.options.storageKey);
    } catch (error) {
      console.warn('Failed to clear navigation state:', error);
    }
  }

  /**
   * Get all state (for debugging)
   */
  getState(): string {
    return {
      ...this.state,
      isExpired: this.isExpired(),
      expiresIn: this.options.expirationTime - (Date.now() - this.state.lastUpdated)
    };
  }

  // ============================================
  // BROWSER-STYLE NAVIGATION (Phase 1)
  // ============================================

  /**
   * Push a new navigation state (navigate forward)
   */
  push(route: string, state: any = {}): any {
    const entry = {
      route,
      state,
      timestamp: Date.now(),
      id: `nav_${Date.now()}_${Math.random()}`
    };

    // If we're not at the end of history, clear forward stack
    if (this.currentIndex < this.navigationStack.length - 1) {
      this.navigationStack = this.navigationStack.slice(0, this.currentIndex + 1);
      this.forwardStack = [];
    }

    // Add to navigation stack
    this.navigationStack.push(entry);
    this.currentIndex = this.navigationStack.length - 1;

    // Limit history size
    if (this.navigationStack.length > this.options.maxHistorySize) {
      this.navigationStack.shift();
      this.currentIndex--;
    }

    // Update current view
    this.setView('route', { route, state });

    if (this.options.autoSave) {
      this.saveNavigationHistory();
    }

    return entry;
  }

  /**
   * Go back in navigation history
   */
  back(): any {
    if (!this.canGoBack()) {
      return null;
    }

    this.currentIndex--;
    const entry = this.navigationStack[this.currentIndex];

    // Update current view
    this.setView('route', { route: entry.route, state: entry.state });

    if (this.options.autoSave) {
      this.saveNavigationHistory();
    }

    return entry;
  }

  /**
   * Go forward in navigation history
   */
  forward(): any {
    if (!this.canGoForward()) {
      return null;
    }

    this.currentIndex++;
    const entry = this.navigationStack[this.currentIndex];

    // Update current view
    this.setView('route', { route: entry.route, state: entry.state });

    if (this.options.autoSave) {
      this.saveNavigationHistory();
    }

    return entry;
  }

  /**
   * Go to a specific position in history (negative = back, positive = forward)
   */
  go(delta: number): any {
    const newIndex = this.currentIndex + delta;

    if (newIndex < 0 || newIndex >= this.navigationStack.length) {
      return null;
    }

    this.currentIndex = newIndex;
    const entry = this.navigationStack[this.currentIndex];

    // Update current view
    this.setView('route', { route: entry.route, state: entry.state });

    if (this.options.autoSave) {
      this.saveNavigationHistory();
    }

    return entry;
  }

  /**
   * Check if can go back
   */
  canGoBack(): boolean {
    return this.currentIndex > 0;
  }

  /**
   * Check if can go forward
   */
  canGoForward(): boolean {
    return this.currentIndex < this.navigationStack.length - 1;
  }

  /**
   * Get current navigation entry
   */
  getCurrentNavigation(): any {
    if (this.currentIndex >= 0 && this.currentIndex < this.navigationStack.length) {
      return this.navigationStack[this.currentIndex];
    }
    return null;
  }

  /**
   * Get navigation history
   */
  getNavigationHistory(): any[] {
    return this.navigationStack;
  }

  /**
   * Clear navigation history
   */
  clearNavigationHistory(): void {
    this.navigationStack = [];
    this.forwardStack = [];
    this.currentIndex = -1;

    if (this.options.autoSave) {
      this.saveNavigationHistory();
    }
  }

  /**
   * Save navigation history to localStorage
   */
  saveNavigationHistory(): void {
    try {
      const historyData = {
        stack: this.navigationStack,
        currentIndex: this.currentIndex
      };
      localStorage.setItem(`${this.options.storageKey}_history`, JSON.stringify(historyData));
    } catch (error) {
      console.warn('Failed to save navigation history:', error);
    }
  }

  /**
   * Load navigation history from localStorage
   */
  loadNavigationHistory(): void {
    try {
      const saved = localStorage.getItem(`${this.options.storageKey}_history`);
      if (saved) {
        const historyData = JSON.parse(saved);
        this.navigationStack = historyData.stack || [];
        this.currentIndex = historyData.currentIndex || -1;
      }
    } catch (error) {
      console.warn('Failed to load navigation history:', error);
    }
  }

  // ============================================
  // MENU MANAGEMENT (Phase 2)
  // ============================================

  /**
   * Register a menu
   */
  registerMenu(id: string, config: any): void {
    this.menus.set(id, {
      id,
      ...config,
      registeredAt: Date.now()
    });

    if (this.options.autoSave) {
      this.saveMenus();
    }
  }

  /**
   * Get a menu by ID
   */
  getMenu(id: string): any {
    return this.menus.get(id) || null;
  }

  /**
   * Get all menus
   */
  getAllMenus(): any[] {
    return Array.from(this.menus.values());
  }

  /**
   * Update a menu item
   */
  updateMenuItem(menuId: string, itemId: string, updates: any): void {
    const menu = this.menus.get(menuId);
    if (!menu) return;

    // Find and update the item (recursive for nested menus)
    const updateItem = (items: any[]) => {
      for (const item of items) {
        if (item.id === itemId) {
          Object.assign(item, updates);
          return true;
        }
        if (item.children && updateItem(item.children)) {
          return true;
        }
      }
      return false;
    };

    if (menu.items) {
      updateItem(menu.items);
    }

    if (this.options.autoSave) {
      this.saveMenus();
    }
  }

  /**
   * Remove a menu
   */
  removeMenu(id: string): void {
    this.menus.delete(id);

    if (this.options.autoSave) {
      this.saveMenus();
    }
  }

  /**
   * Save menus to localStorage
   */
  saveMenus(): void {
    try {
      const menusData = Array.from(this.menus.entries());
      localStorage.setItem(`${this.options.storageKey}_menus`, JSON.stringify(menusData));
    } catch (error) {
      console.warn('Failed to save menus:', error);
    }
  }

  /**
   * Load menus from localStorage
   */
  loadMenus(): void {
    try {
      const saved = localStorage.getItem(`${this.options.storageKey}_menus`);
      if (saved) {
        const menusData = JSON.parse(saved);
        this.menus = new Map(menusData);
      }
    } catch (error) {
      console.warn('Failed to load menus:', error);
    }
  }

  // ============================================
  // BREADCRUMB MANAGEMENT (Phase 3)
  // ============================================

  /**
   * Set breadcrumbs
   */
  setBreadcrumbs(breadcrumbs: any[]): void {
    this.breadcrumbs = breadcrumbs;

    if (this.options.autoSave) {
      this.saveBreadcrumbs();
    }
  }

  /**
   * Get breadcrumbs
   */
  getBreadcrumbs(): any[] {
    return this.breadcrumbs;
  }

  /**
   * Add a breadcrumb
   */
  addBreadcrumb(breadcrumb: any): void {
    this.breadcrumbs.push(breadcrumb);

    if (this.options.autoSave) {
      this.saveBreadcrumbs();
    }
  }

  /**
   * Clear breadcrumbs
   */
  clearBreadcrumbs(): void {
    this.breadcrumbs = [];

    if (this.options.autoSave) {
      this.saveBreadcrumbs();
    }
  }

  /**
   * Auto-generate breadcrumbs from current route
   */
  autoGenerateBreadcrumbs(route: string, labels: Record<string, string> = {}): any[] {
    const parts = route.split('/').filter(p => p);
    const breadcrumbs = [{ label: 'Home', route: '/' }];

    let currentPath = '';
    for (const part of parts) {
      currentPath += `/${part}`;
      breadcrumbs.push({
        label: labels[part] || part.charAt(0).toUpperCase() + part.slice(1),
        route: currentPath
      });
    }

    this.setBreadcrumbs(breadcrumbs);
    return breadcrumbs;
  }

  /**
   * Save breadcrumbs to localStorage
   */
  saveBreadcrumbs(): void {
    try {
      localStorage.setItem(`${this.options.storageKey}_breadcrumbs`, JSON.stringify(this.breadcrumbs));
    } catch (error) {
      console.warn('Failed to save breadcrumbs:', error);
    }
  }

  /**
   * Load breadcrumbs from localStorage
   */
  loadBreadcrumbs(): void {
    try {
      const saved = localStorage.getItem(`${this.options.storageKey}_breadcrumbs`);
      if (saved) {
        this.breadcrumbs = JSON.parse(saved);
      }
    } catch (error) {
      console.warn('Failed to load breadcrumbs:', error);
    }
  }

  // ============================================
  // UTILITY METHODS
  // ============================================

  /**
   * Get relative time string
   */
  static getRelativeTime(timestamp) {
    const now = Date.now();
    const diff = now - timestamp;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (seconds < 60) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    if (days < 30) return `${Math.floor(days / 7)}w ago`;
    return `${Math.floor(days / 30)}mo ago`;
  }
}

export default Navigatr;
